import { useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import type { UserRole } from "../../../api/admin";
import { NAV_GROUPS, type NavGroup, type NavItem } from "../../../router/navigation";
import { useAuth } from "../../../contexts/AuthContext";

const ROLE_RANK: Record<UserRole, number> = {
  admin: 3,
  operator: 2,
  viewer: 1,
};

function readGroupOpen(storageKey?: string) {
  if (!storageKey) return true;
  try {
    return localStorage.getItem(storageKey) !== "closed";
  } catch {
    return true;
  }
}

export function Sidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const isActive = (path: string) =>
    location.pathname === path || location.pathname.startsWith(`${path}/`);

  const canSee = (item: NavItem) => {
    if (!item.minRole) return true;
    if (!user) return false;
    return ROLE_RANK[user.role] >= ROLE_RANK[item.minRole];
  };

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <aside className="w-52 shrink-0 bg-white border-r border-slate-200 flex flex-col h-screen sticky top-0">
      <div className="px-4 py-4 border-b border-slate-200">
        <div className="flex items-center gap-2">
          <span className="w-6 h-6 flex items-center justify-center bg-coral-600 rounded text-white">
            <i className="ri-broadcast-line text-sm" />
          </span>
          <span className="text-xs font-bold text-slate-800 font-mono tracking-tight">
            mobile-admin
          </span>
        </div>
      </div>
      <nav className="flex-1 px-2 py-3 overflow-y-auto">
        {NAV_GROUPS.map((group, index) => (
          <SidebarGroup
            key={group.title}
            group={group}
            first={index === 0}
            canSee={canSee}
            isActive={isActive}
          />
        ))}
      </nav>
      <div className="px-4 py-3 border-t border-slate-200 space-y-2">
        <div className="flex items-center gap-2">
          <span className="w-6 h-6 flex items-center justify-center rounded-full bg-slate-200 text-slate-600 shrink-0">
            <i className="ri-user-line text-xs" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-xs font-medium text-slate-700 truncate">
              {user?.name ?? "—"}
            </p>
            <p className="text-[10px] text-slate-400 capitalize">
              {user?.role ?? ""}
            </p>
          </div>
        </div>
        <button
          onClick={() => void handleLogout()}
          className="flex items-center gap-1.5 w-full px-2 py-1.5 text-xs font-medium text-slate-500 hover:text-red-600 hover:bg-red-50 rounded transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-logout-box-r-line text-sm" />
          Sign out
        </button>
      </div>
    </aside>
  );
}

interface SidebarGroupProps {
  group: NavGroup;
  first: boolean;
  canSee: (item: NavItem) => boolean;
  isActive: (path: string) => boolean;
}

function SidebarGroup({ group, first, canSee, isActive }: SidebarGroupProps) {
  const [open, setOpen] = useState(() => readGroupOpen(group.storageKey));
  const visibleItems = group.items.filter(canSee);
  if (visibleItems.length === 0) return null;

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (group.storageKey) {
      try {
        localStorage.setItem(group.storageKey, next ? "open" : "closed");
      } catch {
        /* ignore storage failures */
      }
    }
  };

  return (
    <div className={first ? "" : "mt-4"}>
      {group.collapsible ? (
        <button
          type="button"
          onClick={toggle}
          aria-expanded={open}
          className="w-full px-2 mb-1 flex items-center justify-between text-[10px] font-semibold text-slate-400 uppercase tracking-wider hover:text-slate-600 cursor-pointer"
        >
          {group.title}
          <i className={`ri-arrow-down-s-line transition-transform ${open ? "" : "-rotate-90"}`} />
        </button>
      ) : (
        <p className="px-2 mb-1 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">
          {group.title}
        </p>
      )}
      {open && (
        <ul className="space-y-0.5">
          {visibleItems.map((item) => (
            <li key={item.path}>
              <NavLink
                to={item.path}
                className={`flex items-center gap-2 px-2 py-1.5 rounded text-xs font-medium transition-colors cursor-pointer ${
                  isActive(item.path)
                    ? "bg-coral-50 text-coral-700"
                    : "text-slate-600 hover:bg-slate-100 hover:text-slate-800"
                }`}
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className={`${item.icon} text-sm`} />
                </span>
                {item.label}
              </NavLink>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
